import styled from "@emotion/styled";
import AddLineItem from "components/NewOrders/AddLineItem/AddLineItem.tsx";
import Paper from "@mui/material/Paper";
import { Alert, Button, Card } from "@mui/material";
import { PageHeader } from "components/Common/Page";

export interface SelectedBoxOptions<Props = Record<string, any>> {
	showBorder?: boolean;
}

export const AddCustomQuoteWrapper = styled(PageHeader)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	padding: 16,
	backgroundColor: theme.project.colors.allColors.hex.grayscale.gray50,
	gap: 16,
	h1: {
		...theme.project.typography.heading.h4,
		color: theme.project.colors.fontColors.main,
		margin: 0
	}
}));

export const QuoteCustomizationsSummaryContainer = styled.div({
	display: "flex",
	flexDirection: "column",
	gap: "16px"
});

export const ErrorText = styled.div(({ theme }) => ({
	color: theme.project.colors.semanticColors.error
}));

export const AddCustomQuoteAlert = styled(Alert)({
	boxSizing: "border-box",
	margin: "16px",
	p: {
		margin: 0
	}
});

export const Heading = styled.h2(({ theme }) => ({
	...theme.project.typography.heading.h5,
	color: theme.project.colors.primaryColors.main,
	margin: 0
}));

export const AddQuoteLineItemGridContainer = styled.div(({ theme }) => ({
	backgroundColor: theme.project.colors.backgroundColors.main,

	h3: {
		...theme.project.typography.subtitle.subtitle1,
		color: theme.project.colors.fontColors.secondary,
		margin: 0
	}
}));

export const RequiredFieldWrapper = styled.div(({ theme }) => ({
	...theme.project.typography.caption,
	color: theme.project.colors.fontColors.secondary
}));

styled.div({
	marginBottom: 10
});

export const AttributesContainer = styled.div(({ theme }) => ({
	display: "flex",
	justifyContent: "space-between",
	button: {
		height: "fit-content",
		minWidth: "fit-content"
	},
	[theme.breakpoints.down("md")]: {
		flexDirection: "column"
	}
}));

export const ItemAndModsContainer = styled.div(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: 16,

	"h4, h5": {
		...theme.project.typography.heading.h6,
		color: theme.project.colors.fontColors.main,
		margin: 0
	}
}));

export const CqModificationsContainer = styled.div({
	gap: "16px",
	display: "flex",
	flexDirection: "column"
});

export const QuotePageFooter = styled.div(({ theme }) => ({
	backgroundColor: theme.project.colors.backgroundColors.tertiary,
	display: "grid",
	gridTemplateColumns: "1fr 1fr",
	gap: 16,
	margin: "16px 0",
	padding: 16,
	[theme.breakpoints.down("md")]: {
		gridTemplateColumns: "1fr"
	}
}));

export const ManualAdjustmentsContainer = styled.div(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: 16,
	maxWidth: 640,
	width: "100%",
	".MuiOutlinedInput-root": {
		backgroundColor: theme.project.colors.backgroundColors.main
	},
	[theme.breakpoints.down("md")]: {
		maxWidth: "100%"
	}
}));

export const QuotePageFooterExport = styled.div(({ theme }) => ({
	display: "flex",
	justifyContent: "end",
	gap: 24,
	[theme.breakpoints.down("md")]: {
		button: { width: "100%" }
	}
}));

export const ChargesContainer = styled.div<{ isUpcharge?: boolean }>(({ theme, isUpcharge }) => ({
	div: {
		display: isUpcharge ? "grid" : "flex",
		gridTemplateColumns: isUpcharge ? "7fr 1fr" : undefined,
		justifyContent: "end",
		gap: 16,
		color: theme.project.colors.fontColors.main,
		"&:first-of-type > span:first-of-type": {
			...theme.project.typography.alert.alertTitle
		},
		...(isUpcharge && {
			textAlign: "right",
			"&:nth-of-type(2) > span:first-of-type": {
				...theme.project.typography.alert.alertTitle
			},
			"&:last-of-type": {
				marginTop: 16,
				padding: 16,
				backgroundColor: theme.project.colors.backgroundColors.accordionSummary
			}
		}),
		"&:first-of-type > span:last-of-type": {
			...theme.project.typography.body.body1
		},
		"&:last-of-type > span": {
			...theme.project.typography.heading.h6,
			color: theme.project.colors.primaryColors.main
		},
		[theme.breakpoints.down("md")]: {
			justifyContent: "space-between"
		}
	}
}));

export const StyledAddLineItem = styled(AddLineItem)(({ theme }) => ({
	"& > div": {
		border: "unset",
		margin: 0,
		padding: 0
	},
	[theme.breakpoints.down("md")]: {
		button: { width: "100%" }
	}
}));

export const SelectedBox = styled(Paper, {
	shouldForwardProp: (prop) => prop !== "showBorder"
})<SelectedBoxOptions>(({ showBorder, theme }) => ({
	border: showBorder ? `1px solid ${theme.project.colors.primaryColors.main}` : "none",
	borderRadius: 0,
	padding: 16
}));

export const Content = styled.div(({ theme }) => ({
	backgroundColor: theme.project.colors.backgroundColors.tertiary,
	boxSizing: "border-box",
	display: "flex",
	flexDirection: "column",
	gap: theme.project.layout.gap.large,
	margin: "0 16px",
	width: "calc(100% - 32px)",
	padding: 16
}));

export const BaseItemEdit = styled.div({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	marginBottom: "16px"
});

export const SendQuoteButton = styled(Button)({
	display: "flex",
	alignItems: "center"
});

export const SpecialModificationNotes = styled.div(({ theme }) => ({
	...theme.project.typography.body.body1,
	fontWeight: 500,
	color: theme.project.colors.fontColors.secondary,
	display: "flex",
	flexDirection: "column",
	marginTop: 8,
	"& > div": {
		...theme.project.typography.body.body2
	}
}));

export const NoModificationsSelected = styled(Card)(({ theme }) => ({
	...theme.project.typography.body.body1,
	fontWeight: 500,
	border: theme.project.borders.presets.thinOutline,
	boxShadow: "none",
	color: theme.project.colors.fontColors.secondary,
	marginTop: 8,
	backgroundColor: theme.project.colors.backgroundColors.card,
	borderRadius: "unset",
	padding: 16
}));

export const SendQuoteButtonsContainer = styled(QuotePageFooterExport)(({ theme }) => ({
	gap: 24
}));

export const CQModificationContainer = styled.div(({ theme }) => ({
	display: "flex",
	gap: "16px",
	"> div:first-of-type": {
		flex: "1"
	}
}));

export const RangesContainer = styled.div(({ theme }) => ({
	display: "flex",
	gap: "16px",
	flex: "1",
	"> div": {
		flex: 1
	}
}));
